import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction } from "react-router";
import { useFirebaseRefreshToken } from "./useFirebaseRefreshToken";
import { useFirebaseSignIn } from "./useFirebaseSignIn";

export interface Auth {
  isAuth: boolean;
  organizationId: string;
  authInfo: LoginSuccessResponse;
  signin: (
    email: string,
    password: string,
    navigate: NavigateFunction,
    onSuccess: () => void,
    onError: () => void,
    path: string
  ) => Promise<boolean>;
  signout: (navigate: NavigateFunction) => boolean;
  apiError: () => boolean;
  refreshAccessToken: () => Promise<boolean>;
  loading: boolean;
}

export interface LoginSuccessResponse {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
}

const AuthContext = createContext<Auth | null>(null);

type Props = {
  children: JSX.Element;
};

export function ProvideAuth({ children }: Props) {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  return useContext(AuthContext);
};

function useProvideAuth(): Auth {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [organizationId] = useState("");
  const [authInfo, setAuthInfo] = useState<LoginSuccessResponse>({
    accessToken: "",
    refreshToken: "",
    idToken: "",
  });

  const { signIn: firebaseSignIn } = useFirebaseSignIn({
    onSuccess: (firebaseResponse) => {
      setAuthInfo({
        accessToken: firebaseResponse.idToken,
        refreshToken: firebaseResponse.refreshToken,
        idToken: firebaseResponse.idToken,
      });
      setIsAuth(true);
    },
  });

  const { refreshToken: firebaseRefreshToken } = useFirebaseRefreshToken({
    onSuccess: (firebaseResponse) => {
      setAuthInfo({
        accessToken: firebaseResponse.id_token,
        refreshToken: firebaseResponse.refresh_token,
        idToken: firebaseResponse.id_token,
      });
      setIsAuth(true);
    },
    onError: () => {
      // Refresh failed, clear auth state
      setAuthInfo({ accessToken: "", refreshToken: "", idToken: "" });
      window.localStorage.removeItem("authInfo");
      setIsAuth(false);
    },
  });

  useEffect(() => {
    setLoading(true);
    const localAuthInfo: LoginSuccessResponse = JSON.parse(
      window.localStorage.getItem("authInfo")
    );
    if (localAuthInfo && localAuthInfo.accessToken) {
      setAuthInfo(localAuthInfo);
      setIsAuth(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authInfo.accessToken !== "") {
      window.localStorage.setItem("authInfo", JSON.stringify(authInfo));
    }
  }, [authInfo]);

  const refreshAccessToken = useCallback(async () => {
    try {
      if (!authInfo.refreshToken) {
        return false;
      }

      const response = await firebaseRefreshToken(authInfo.refreshToken);

      if (response) {
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }, [authInfo.refreshToken, authInfo.accessToken, firebaseRefreshToken]);

  // Auto-refresh token every 30 minutes when authenticated
  useEffect(() => {
    if (!isAuth || !authInfo.refreshToken) {
      return undefined;
    }

    const intervalId = setInterval(async () => {
      await refreshAccessToken();
    }, 30 * 60 * 1000); // 30 minutes in milliseconds

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuth, authInfo.refreshToken, refreshAccessToken]);

  const signin = async (
    email: string,
    password: string,
    navigate: NavigateFunction,
    onSuccess: () => void,
    onError: () => void,
    path: string
  ) => {
    try {
      const success = await firebaseSignIn(email, password);

      if (success) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).gtag("event", "publisher_login_success", {
          event_category: "publisher_login",
          email: email,
        });
        console.log(t("hooks.login_success"));
        onSuccess();
        navigate(path);
        return true;
      }

      onError();
      return false;
    } catch (error) {
      onError();
      return false;
    }
  };

  const signout = (navigate: NavigateFunction) => {
    setAuthInfo({ accessToken: "", refreshToken: "", idToken: "" });
    window.localStorage.removeItem("authInfo");
    setIsAuth(false);
    navigate("/signin");
    return true;
  };

  const apiError = () => {
    setAuthInfo({ accessToken: "", refreshToken: "", idToken: "" });
    window.localStorage.removeItem("authInfo");
    setIsAuth(false);
    window.location.href = "/signin";
    return true;
  };

  // Return the user object and auth methods
  return {
    isAuth,
    organizationId,
    authInfo,
    signin,
    signout,
    apiError,
    refreshAccessToken,
    loading,
  };
}
